import { useCallback, useEffect, useState } from "react";
import { NavLink, Route, Switch, useParams } from "react-router-dom";

import { useFirebase } from "@meddbriefer/mdb-firebase";

import {
	getScenarioTypeObject,
	getDocument,
	saveDocument
} from "@meddbriefer/scenario-data/index.js";

import { updateSchema } from "./traumaScenario.js";

import { ScenarioEditorContext } from "./ScenarioEditorContext";
import { useHeader } from "../HeaderContext.js";

import BasicInfo from "./BasicInfo.js";
import PatientStatus from "./PatientStatus.js";
import Steplist from "./Steplist.js";
import CheckListCorrectness from "./CheckListCorrectness.js";
import Medications from "./Medications.js";
import InterventionAnswers from "./InterventionAnswers.js";
import ProblemsList from "./ProblemsList"
import Problem2 from "./Problem2";
import Solution2 from "./Solution2";

import { appendObjToArray, updateObjInArray, removeObjWithIdFromArray } from "../ScenarioTypeEditor/utils";

const COLLECTION_ID = 'scenarios';

export default function EditScenario() {
	const {db} = useFirebase()
	const { scenarioId } = useParams()
	const {setTitle} = useHeader()
	const [scenario, setScenario] = useState(null);
	const [scenarioTypeData, setScenarioTypeData] = useState(null)
	const [uniqueIDs, setUniqueIDs] = useState(null)

	const updateScenarioFld = (fldName, fldVal) => {
		setScenario(prevState => ({...prevState, [fldName]: fldVal}))
	}

	const problems = {
		add: (obj) => updateScenarioFld("problems", appendObjToArray(scenario.problems, obj)),
		update: (obj) => updateScenarioFld("problems", updateObjInArray(scenario.problems, obj)),
		remove: (id) => updateScenarioFld("problems", removeObjWithIdFromArray(scenario.problems, id))
	}

	const solutions = {
		add: (obj) => updateScenarioFld("solutions", appendObjToArray(scenario.solutions, obj)),
		update: (obj) => updateScenarioFld("solutions", updateObjInArray(scenario.solutions, obj)),
		remove: (id) => updateScenarioFld("solutions", removeObjWithIdFromArray(scenario.solutions, id))
	}

	const actions = {
		add: (obj) => updateScenarioFld("actions", appendObjToArray(scenario.actions, obj)),
		update: (obj) => updateScenarioFld("actions", updateObjInArray(scenario.actions, obj)),
		remove: (id) => updateScenarioFld("actions", removeObjWithIdFromArray(scenario.actions, id))
	}

	const saveScenario = async (scenData) => {
		// strips fields whose values are "undefined". there *must* be a better way
		// I imagine better field validation is in order
		const data = JSON.parse(JSON.stringify(scenData))
		// setScenario(data)
		await saveDocument(db, "scenarios", data)
		alert("changes saved")
	}

	const loadScenario = useCallback(
		() => {
			const load = async () => {
				const scenData = await getDocument(db, COLLECTION_ID, scenarioId)
				const scenTypeData = await getScenarioTypeObject(db, scenData.scenarioType, scenData.schemaVersion)
				// make use of scenTypeData and other sources to ensure data from db is in sync
				// with latest schema
				const scen = updateSchema(scenData, scenTypeData)
				setTitle(`Scenario: ${scen.name}`)
				setScenarioTypeData(scenTypeData)
				setScenario(scen)
			}
			load()
		}, [db, scenarioId, setTitle]
	)

	// Load and apply selected scenario
	useEffect(() => {
		if (!scenario || scenario.id !== scenarioId) {
			loadScenario()
		}
	}, [scenarioId, setTitle, scenario, loadScenario]);

	useEffect(
		()=> {
			if (scenario) {
				// console.log(scenario)
				let ids = [...scenarioTypeData.uniqueIDs]
				ids = ids.concat(scenario.problems.map(e => e.id))
				scenario.problems.forEach(p => {
					// ids = ids.concat(p.assessments.map(e => e.id))
					ids = ids.concat(p.notes.map(e => e.id))
				})
				ids = ids.concat(scenario.solutions.map(e => e.id))
				// ids = ids.concat(scenario.actions.map(e => e.id))
				setUniqueIDs(ids)
			}
		}, [scenarioTypeData, scenario]
	)
	if (!scenario || !scenarioTypeData) {
		return (<div>Loading...</div>)
	} else {
		window.scenario = scenario
		window.scenarioType = scenarioTypeData
	}

	return (
		<div className="scenario-editor">
			<nav id="tab-selector">
				<NavLink
					to={`/scenario/${scenarioId}/basic-info`}
					className="tab-button"
					activeClassName="active-tab"
				>
					Info
				</NavLink>
				<NavLink
					to={`/scenario/${scenarioId}/patient-status`}
					className="tab-button"
					activeClassName="active-tab"
				>
					Patient Status
				</NavLink>
				<NavLink
					to={`/scenario/${scenarioId}/assessment-findings`}
					className="tab-button"
					activeClassName="active-tab"
				>
					Assessment Findings
				</NavLink>
				<NavLink
					to={`/scenario/${scenarioId}/checklist-correctness`}
					className="tab-button"
					activeClassName="active-tab"
				>
					Checklist Correctness
				</NavLink>
				<NavLink
					to={`/scenario/${scenarioId}/medications`}
					className="tab-button"
					activeClassName="active-tab"
				>
					Medications
				</NavLink>
				<NavLink
					to={`/scenario/${scenarioId}/problems`}
					className="tab-button"
					activeClassName="active-tab"
				>
					Problems
				</NavLink>
				<NavLink
					to={`/scenario/${scenarioId}/intervention-answers`}
					className="tab-button"
					activeClassName="active-tab"
				>
					Intervention Answers
				</NavLink>
			</nav>
			<ScenarioEditorContext.Provider
				value={{
					scenario,
					updateScenarioFld,
					problems,
					solutions,
					actions,
					uniqueIDs,
					uniqueLabels: scenarioTypeData.uniqueLabels,
					saveScenario,
					scenarioType: scenarioTypeData
				}}
			>

			<Switch>
				<Route path="/scenario/:scenarioId/basic-info" component={BasicInfo} />
				<Route path="/scenario/:scenarioId/patient-status" component={PatientStatus} />
				<Route path="/scenario/:scenarioId/assessment-findings" component={Steplist} />
				<Route path="/scenario/:scenarioId/checklist-correctness" component={CheckListCorrectness} />
				<Route path="/scenario/:scenarioId/medications" component={Medications} />
				<Route path="/scenario/:scenarioId/problems/:probID/solutions/:solID" component={Solution2} />
				<Route path="/scenario/:scenarioId/problems/:probID" component={Problem2} />
				<Route path="/scenario/:scenarioId/problems" component={ProblemsList} />
				<Route path="/scenario/:scenarioId/intervention-answers" component={InterventionAnswers}/>
				<Route path="/scenario/:scenarioId" component={BasicInfo} />
			</Switch>

		</ScenarioEditorContext.Provider>
		</div>

	)

}
